import { useEffect, useMemo, useRef, useState } from 'react';
import { filterTimeZones, matchTimeZone } from '@project/event-creator';

import { EN_MESSAGES, fill, type EventCreatorMessages } from './messages';

export interface TimeZoneChoice {
  readonly zone: string;
  /** The text shown in the input after a choice is made. */
  readonly displayLabel: string;
  /** The text shown in the suggestion list, including the event-time offset. */
  readonly optionLabel: string;
}

export interface TimeZonePickerProps {
  readonly id: string;
  readonly selectedZone?: string;
  readonly choices: readonly TimeZoneChoice[];
  readonly onChange: (zone: string | undefined) => void;
  /** The language the choices are named in, so search matches what the list actually shows. */
  readonly locale?: string;
  /** The two strings this control says of its own. Already merged over English by the form. */
  readonly messages?: EventCreatorMessages;
}

/**
 * A small accessible combobox around the headless zone search. A native datalist only searches
 * option values, which made friendly labels and country names decorative rather than searchable;
 * this keeps the input human-readable while the callback still receives the IANA identifier.
 */
export function TimeZonePicker({
  id,
  selectedZone,
  choices,
  onChange,
  locale,
  messages = EN_MESSAGES,
}: TimeZonePickerProps): React.JSX.Element {
  const selectedChoice = choices.find((choice) => choice.zone === selectedZone);
  const [query, setQuery] = useState(selectedChoice?.displayLabel ?? selectedZone ?? '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const editing = useRef(false);

  // A draft can be loaded by the host while this control is mounted. Do not replace text someone
  // is currently searching for, but follow a completed external selection immediately.
  useEffect(() => {
    if (editing.current) return;
    const choice = choices.find((entry) => entry.zone === selectedZone);
    setQuery(choice?.displayLabel ?? selectedZone ?? '');
  }, [choices, selectedZone]);

  const matchingChoices = useMemo(() => {
    const zones = filterTimeZones(
      choices.map((choice) => choice.zone),
      query,
      locale,
    );
    const shown = zones.slice(0, 8);
    if (
      selectedZone !== undefined &&
      !shown.includes(selectedZone) &&
      zones.includes(selectedZone)
    ) {
      shown.push(selectedZone);
    }
    return shown
      .map((zone) => choices.find((choice) => choice.zone === zone))
      .filter((choice): choice is TimeZoneChoice => choice !== undefined);
  }, [choices, locale, query, selectedZone]);

  const choose = (choice: TimeZoneChoice): void => {
    editing.current = false;
    setQuery(choice.displayLabel);
    setOpen(false);
    setActiveIndex(0);
    onChange(choice.zone);
  };

  const onInputChange = (value: string): void => {
    editing.current = true;
    setQuery(value);
    setOpen(true);
    setActiveIndex(0);
    // Emptying the box is how someone asks for a floating time, so that still clears the zone.
    // Text that merely hasn't resolved yet does not: `germ` on the way to `Germany` is a query in
    // progress, and treating it as "no zone" silently turned a zoned event into a floating one
    // while the box still showed what had been typed.
    if (value.trim() === '') {
      onChange(undefined);
      return;
    }
    const zone = matchTimeZone(
      value,
      choices.map((choice) => choice.zone),
      locale,
    );
    if (zone === undefined) return;
    const choice = choices.find((entry) => entry.zone === zone);
    if (choice !== undefined) {
      editing.current = false;
      setQuery(choice.displayLabel);
      onChange(zone);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(matchingChoices.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const choice = matchingChoices[activeIndex];
      if (choice !== undefined) choose(choice);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      editing.current = false;
      setQuery(selectedChoice?.displayLabel ?? selectedZone ?? '');
      setOpen(false);
      setActiveIndex(0);
    }
  };

  const listId = `${id}-options`;
  const activeChoice = matchingChoices[activeIndex];
  return (
    <div className="ec-autocomplete">
      <input
        id={id}
        type="text"
        role="combobox"
        placeholder={messages.zoneSearchPlaceholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={
          open && activeChoice !== undefined ? `${listId}-${activeChoice.zone}` : undefined
        }
        value={query}
        onFocus={() => {
          setOpen(true);
          setActiveIndex(0);
        }}
        onBlur={() => {
          setOpen(false);
          editing.current = false;
          // Whatever the zone actually is now, spelled the way this control spells it. Falling
          // back to `query` here left an abandoned search sitting in the box as though it were a
          // selection — the field read `germ` while the event was in New York.
          const choice = choices.find((entry) => entry.zone === selectedZone);
          setQuery(choice?.displayLabel ?? selectedZone ?? '');
        }}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul id={listId} className="ec-autocomplete-list" role="listbox">
          {matchingChoices.length === 0 ? (
            <li className="ec-autocomplete-empty" role="status">
              {fill(messages.zoneNoMatch, { query })}
            </li>
          ) : (
            matchingChoices.map((choice, index) => (
              <li
                id={`${listId}-${choice.zone}`}
                key={choice.zone}
                className="ec-autocomplete-option"
                role="option"
                aria-selected={choice.zone === selectedZone}
                data-active={index === activeIndex ? 'true' : undefined}
                onMouseDown={(event) => {
                  // Keep the input focused long enough for a click to select a result.
                  event.preventDefault();
                  choose(choice);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {choice.optionLabel}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
